import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PaypointResponse, RoutingRuleResponse } from '../models/paypoint-response.interface';
import { PaypointRequest, RoutingRuleRequest } from '../models/paypoint-request.interface';
import { PaymentMethodResponse } from '../models/payment-method-response.interface';
import { PaymentMethodRequest } from '../models/payment-method-request.interface';
import { ProcessPaymentResponse } from '../models/process-payment-response.interface';
import { environment } from 'src/environments/environment';
import { SuccessResponse } from '../models/responses.interface';
import { Transaction } from '../models/transaction.interface';
import { BillingContactResponse } from '../models/contact.interface';
import { Payment } from '../models/process-payment-request.interface';
import { select, Store } from '@ngrx/store';
import { currentUser } from '../../auth';
import { AppState } from '../../reducers';
import { QueryParamsModel } from '../../_base/crud';
import { isEqual, uniqWith } from 'lodash-es';

@Injectable()
export class PaypointService {
  private readonly baseUrl = `${environment.BRIDGEPAY_BASE}/v1`;
  private readonly coreBaseUrl = `${environment.CORE_BASE}`;
  private companyUUID: string;
  private companyID: number;
  mappedContacts = [];

  constructor(private http: HttpClient, private store: Store<AppState>) {
    this.store.pipe(select(currentUser)).subscribe((res) => {
      this.companyUUID = res.companyUUID;
      this.companyID = res.companyID;
    });
  }

  /** Paypoint APIs */
  getOfficePaypoints(
    organization: string,
    officeId: string,
  ): Observable<SuccessResponse<PaypointResponse[]>> {
    return this.http
      .get<SuccessResponse<PaypointResponse[]>>(
        `${this.baseUrl}/${organization}/Paypoint/${officeId}`,
      )
      .pipe(catchError(this.handleError));
  }

  addPaypoint(
    organization: string,
    payload: PaypointRequest,
  ): Observable<SuccessResponse<PaypointResponse>> {
    return this.http
      .post<SuccessResponse<PaypointResponse>>(`${this.baseUrl}/${organization}/Paypoint`, payload)
      .pipe(catchError(this.handleError));
  }

  verifyCredentials(
    organization: string,
    payload: PaypointRequest,
  ): Observable<SuccessResponse<PaypointResponse>> {
    return this.http
      .post<SuccessResponse<PaypointResponse>>(
        `${this.baseUrl}/${organization}/Paypoint/verify`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  addRoutingRule(
    organization: string,
    officeId: string,
    payload: RoutingRuleRequest,
  ): Observable<SuccessResponse<RoutingRuleResponse>> {
    return this.http
      .post<SuccessResponse<RoutingRuleResponse>>(
        `${this.baseUrl}/${organization}/routing-rule/${officeId}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  getRoutingRule(
    organization: string,
    officeId: string,
  ): Observable<SuccessResponse<PaymentMethodResponse[]>> {
    return this.http
      .get<SuccessResponse<PaymentMethodResponse[]>>(
        `${this.baseUrl}/${organization}/routing-rule/${officeId}`,
      )
      .pipe(catchError(this.handleError));
  }
  deleteRoutingRule(organization: string, ruleCode: string) {
    return this.http.delete(`${this.baseUrl}/${organization}/routing-rule/${ruleCode}`);
  }

  /** Payment Method APIs */
  getPaymentMethods(
    organization: string,
    customerId: string,
  ): Observable<SuccessResponse<PaymentMethodResponse[]>> {
    return this.http
      .get<SuccessResponse<PaymentMethodResponse[]>>(
        `${this.baseUrl}/${organization}/PaymentMethod/${customerId}`,
      )
      .pipe(catchError(this.handleError));
  }

  createPaymentMethod(
    organization: string,
    customerId: string,
    payload: PaymentMethodRequest,
  ): Observable<SuccessResponse<PaymentMethodResponse>> {
    return this.http
      .post<SuccessResponse<PaymentMethodResponse>>(
        `${this.baseUrl}/${organization}/PaymentMethod/${customerId}`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }

  /** Payment APIs */
  processPayment(
    organization: string,
    payload: Payment,
  ): Observable<SuccessResponse<ProcessPaymentResponse>> {
    return this.http
      .post<SuccessResponse<ProcessPaymentResponse>>(
        `${this.baseUrl}/${organization}/Payment`,
        payload,
      )
      .pipe(catchError(this.handleError));
  }
  /** Payment by source type and source ID APIs */
  getPaymentBySourceTypeAndSourceID(
    organization: string,
    source_type: string,
    source_ID: string,
  ): Observable<SuccessResponse<Transaction>> {
    return this.http
      .get<SuccessResponse<Transaction>>(
        `${this.baseUrl}/${organization}/Payment/source/${source_type}/${source_ID}`,
      )
      .pipe(catchError(this.handleError));
  }

  getContactsByUUIDs(contact_uuids: any[]) {
    if (contact_uuids.length === 0) {
      return of({});
    }
    let UUIDs = '';
    // tslint:disable-next-line:no-shadowed-variable
    contact_uuids = uniqWith(contact_uuids, isEqual);
    contact_uuids.forEach((element) => (UUIDs = UUIDs + '&contact_uuids=' + element));
    const params = new HttpParams({ fromString: UUIDs });
    return this.http.get<any>(
      `${this.coreBaseUrl}/companies/${this.companyID}/contacts?page=0&size=` +
        contact_uuids.length,
      {
        params,
      },
    );
  }

  mappedContactFromListGetName(uuid) {
    const contact = this.getContactFromMappingList()?.find((s) => s.contact_uuid === uuid);
    if (!contact) {
      return '';
    }

    return [contact.first_name, contact.middle_name, contact.last_name, contact.last_name2]
      .filter((name) => name?.trim())
      .join(' ');
  }
  mappedContactFromListGetID(uuid) {
    const contact = this.getContactFromMappingList()?.find((s) => s.contact_uuid === uuid);
    if (!contact) {
      return '';
    }
    return '(' + contact.id + ')';
  }

  getContactFromMappingList() {
    return this.mappedContacts;
  }

  initiateContactMappingList(contacts) {
    this.mappedContacts = contacts;
  }

  private static getTransactionQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) fromString += `&searchTerm=${filter.searchTerm}`;
    if (filter.status) fromString += `&status=${filter.status}`;
    if (filter.amount) fromString += `&amount=${filter.amount}`;
    if (filter.paymentCode) fromString += `&transactionCode=${filter.paymentCode}`;
    if (filter.dateTime) fromString += `&createdAt=${filter.dateTime}`;
    if (filter.contact) fromString += `&customer_id=${filter.contact.contact_uuid}`;

    return fromString;
  }

  /** Transaction APIs */
  getTransactions(queryParams: QueryParamsModel): Observable<{ data: any }> {
    const params = new HttpParams({
      fromString: PaypointService.getTransactionQueryString(queryParams),
    });
    return this.http.get<{ data: any }>(
      `${this.baseUrl}/${this.companyUUID}/Payment/transactions`,
      { params },
    );
  }

  /** Void API */
  voidTransaction(payment_code, void_reason): Observable<SuccessResponse<any>> {
    return this.http.post<SuccessResponse<any>>(
      `${this.baseUrl}/${this.companyUUID}/Payment/${payment_code}/void`,
      { voidReason: void_reason },
    );
  }

  /** Payment Method APIs */
  removePaymentMethod(
    organization: string,
    customerId: string,
    methodCode: string,
  ): Observable<SuccessResponse<PaymentMethodResponse>> {
    return this.http.delete<SuccessResponse<PaymentMethodResponse>>(
      `${this.baseUrl}/${organization}/PaymentMethod/${customerId}/${methodCode}`,
    );
  }

  setDefaultPaymentMethod(
    organization: string,
    customerId: string,
    methodCode: string,
  ): Observable<SuccessResponse<PaymentMethodResponse>> {
    return this.http.post<SuccessResponse<PaymentMethodResponse>>(
      `${this.baseUrl}/${organization}/PaymentMethod/${customerId}/set-default/${methodCode}`,
      {},
    );
  }

  /**
   * Fetch the billing contact for the given company and entity IDs.
   * @param entityIds The list of entity IDs.
   * @returns An observable of the response.
   */
  getBillingContact(companyId: number, entityIds: number[]): Observable<BillingContactResponse> {
    const url = `${this.coreBaseUrl}/companies/${companyId}/contacts/billing-contact`;
    const body = { entity_ids: entityIds };

    return this.http.post<BillingContactResponse>(url, body);
  }

  /** Error handler */
  private handleError(error: HttpErrorResponse): Observable<never> {
    return throwError(new Error(error?.error?.message || 'An unknown error occurred'));
  }
}
